import { useMutation } from '@tanstack/react-query';
import { ApiConfig } from '@/config/api.config';
import { useAxiosAuth } from '@/hooks/useAxiosAuth';
import { useTranslation } from 'react-i18next';
import EToast from '@/components/everridge/EToast';
import { CreateDatabaseBackupResponseDto } from '@/features/superAdmin/features/tenants/types/superAdmin-tenants-dto';

export const useTenantDatabaseBackupApi = () => {
	const axiosInstance = useAxiosAuth();
	const { t } = useTranslation();

	const { mutateAsync: createDatabaseBackup, isPending: isCreatingBackup } =
		useMutation({
			mutationFn: async (): Promise<CreateDatabaseBackupResponseDto> => {
				const response =
					await axiosInstance.post<CreateDatabaseBackupResponseDto>(
						ApiConfig.queries.superAdmin.tenants.endpoints
							.createDatabaseBackup,
					);
				return response.data;
			},
			onError: () => {
				EToast({
					variant: 'error',
					message: t(
						'main.superAdmin.tenants.components.databaseBackup.errorMessage',
					),
					description: t(
						'main.superAdmin.tenants.components.databaseBackup.errorDescription',
					),
				});
			},
		});

	const downloadDatabaseBackup = async (
		backupId: string,
		fileName: string,
	) => {
		const url = ApiConfig.queries.superAdmin.tenants.endpoints.downloadDatabaseBackup.replace(
			'{backupId}',
			backupId,
		);
		const { data } = await axiosInstance.get<Blob>(url, {
			responseType: 'blob',
		});
		const href = URL.createObjectURL(data);
		const anchor = document.createElement('a');
		anchor.href = href;
		anchor.download = fileName;
		anchor.click();
		URL.revokeObjectURL(href);
	};

	const createAndDownloadBackup = async () => {
		const backup = await createDatabaseBackup();
		await downloadDatabaseBackup(backup.backupId, backup.fileName);
		EToast({
			variant: 'success',
			message: t(
				'main.superAdmin.tenants.components.databaseBackup.successMessage',
			),
			description: t(
				'main.superAdmin.tenants.components.databaseBackup.successDescription',
			),
		});
		return backup;
	};

	return {
		createAndDownloadBackup,
		isCreatingBackup,
	};
};
